/**
 * What ONDO will accept as a password, in one place.
 *
 * ## It is here because there were already three copies of the number
 *
 * `MIN_ACCOUNT_PASSWORD_LENGTH` in the API's signup schema, `MIN_PASSWORD_LENGTH` in
 * the web's signup constants, and a sentence in each quoting "8". Password recovery
 * needed a fourth on the website and a fifth on the phone, which is the point at
 * which a duplicated rule becomes a drifting one — and the drift is silent: the form
 * says one number, the server enforces another, and the symptom is a refusal nobody
 * can act on because the message quotes the wrong figure.
 *
 * This is `packages/shared/src/domain`'s stated job — vocabulary that several surfaces
 * must agree about.
 *
 * ## EIGHT IS OURS. SUPABASE'S FLOOR IS LOWER, AND THAT IS NOT A CONTRADICTION
 *
 * `minimum_password_length = 6` in `supabase/config.toml` is GoTrue's own refusal
 * threshold, and it is deliberately left where it is: it is the last line, and
 * loosening our rule to match it would be the wrong direction. **The consequence worth
 * knowing is that GoTrue will accept a seven-character password that this rule
 * refuses**, so anything calling `updateUser({ password })` or `admin.createUser`
 * without checking here is a door into a weaker password than the forms allow. The two
 * reset screens and signup are the three doors, and all three check.
 *
 * ## There is no complexity rule, and that is a decision
 *
 * `password_requirements` is empty in the config. A mixed-case-and-a-symbol rule
 * produces `Summer2026!` reliably and nothing else, and length is the only property
 * that reliably costs an attacker anything. The honest advice is on the screens —
 * "choose a password you do not use anywhere else" — rather than in a validator that
 * would reject a good passphrase for having no digit.
 */
/** The shortest password ONDO will take. Quoted in copy; never inlined beside it. */
export const MIN_PASSWORD_LENGTH = 8;
/**
 * Check a password against its confirmation.
 *
 * **Length is checked first, and the order is the whole of the function.** Told "those
 * do not match" about two strings that are both too short, somebody fixes the wrong
 * thing — and then meets the length refusal anyway, having retyped both fields. The
 * server refuses on length regardless, so this is the same rule said earlier rather
 * than a second rule.
 */
export function refusePassword(password, confirmation) {
    if (password.length < MIN_PASSWORD_LENGTH)
        return "too-short";
    if (password !== confirmation)
        return "mismatch";
    return null;
}
//# sourceMappingURL=password.js.map